import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Bell, ChevronDown, X, Truck, ShieldCheck, Menu, Volume2, VolumeX } from 'lucide-react';
import { cn, formatName, formatRole } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import { useNotification } from '../../context/NotificationContext';
import { useSettings } from '../../context/SettingsContext';
import appConfig from '../../config/appConfig';

// Generate consistent color based on user name
const getAvatarColor = (name) => {
  const colors = [
    'bg-blue-600', 'bg-green-600', 'bg-purple-600', 'bg-pink-600',
    'bg-indigo-600', 'bg-teal-600', 'bg-orange-600', 'bg-red-600',
    'bg-cyan-600', 'bg-emerald-600', 'bg-violet-600', 'bg-rose-600'
  ];
  if (!name) return colors[0];
  const charSum = name.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0);
  return colors[charSum % colors.length];
};

// Get user initials
const getInitials = (name) => {
  if (!name) return 'U';
  const parts = name.trim().split(' ');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

export const TopBar = ({ onMenuClick }) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const { user, travelsInfo } = useAuth();
  const { settings } = useSettings();
  const initials = getInitials(user?.name);

  // Get colors from settings - topbar uses sidebar color to match
  const sidebarColor = settings?.sidebarColor || '#1F2937';
  const primaryColor = settings?.primaryColor || '#3B82F6';

  const {
    notifications,
    unreadCount,
    newNotification,
    markAsRead,
    markAllAsRead,
    clearNewNotification,
    requestNotificationPermission,
    soundEnabled,
    toggleSound
  } = useNotification();

  // Request browser notification permission on mount
  useEffect(() => {
    if (requestNotificationPermission) {
      requestNotificationPermission();
    }
  }, [requestNotificationPermission]);

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'assignment':
        return <Truck className="h-4 w-4 text-blue-600" />;
      case 'booking':
        return <Bell className="h-4 w-4 text-green-600" />;
      case 'verification':
        return <ShieldCheck className="h-4 w-4 text-orange-600" />;
      default:
        return <Bell className="h-4 w-4 text-gray-600" />;
    }
  };

  const formatTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    if (seconds < 60) return 'Just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  return (
    <>
      {/* New Notification Toast */}
      {newNotification && (
        <div className="fixed top-4 right-4 z-50 animate-slide-in-right">
          <div className="bg-white rounded-lg shadow-xl border border-gray-200 p-4 max-w-sm">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                {getNotificationIcon(newNotification.type)}
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-900">
                  {newNotification.title}
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  {newNotification.message}
                </p>
              </div>
              <button
                onClick={clearNewNotification}
                className="flex-shrink-0 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      <header
        className="fixed top-0 left-0 md:left-64 right-0 h-16 border-b z-30"
        style={{ backgroundColor: sidebarColor, borderColor: 'rgba(255,255,255,0.1)' }}
      >
      <div className="h-full px-4 md:px-6 flex items-center justify-between">
        {/* Hamburger menu - mobile only */}
        <button
          onClick={onMenuClick}
          className="md:hidden p-2 text-white hover:bg-white/10 rounded-lg mr-2"
        >
          <Menu className="h-6 w-6" />
        </button>

        {/* Travels name/logo - mobile only */}
        <Link to="/dashboard" className="md:hidden flex items-center flex-1 min-w-0 gap-2 hover:opacity-90 transition-opacity">
          {user?.role === 'admin' ? (
            <>
              <img src={appConfig.logo} alt={appConfig.name} className="h-8 w-8 object-contain rounded" />
              <span className="text-sm font-bold text-white truncate">{appConfig.name}</span>
            </>
          ) : travelsInfo?.logo?.url ? (
            <>
              <img src={travelsInfo.logo.url} alt={travelsInfo.name} className="h-8 w-8 rounded-full object-cover" />
              <span className="text-sm font-bold text-white truncate">{travelsInfo.name}</span>
            </>
          ) : (
            <span className="text-sm font-bold text-white truncate">
              {travelsInfo?.name || appConfig.name}
            </span>
          )}
        </Link>

        {/* Search - hidden on mobile */}
        <div className="hidden md:block flex-1 max-w-2xl">
          <form onSubmit={(e) => {
            e.preventDefault();
            if (searchTerm.trim()) {
              navigate(`/cars?search=${encodeURIComponent(searchTerm.trim())}`);
            }
          }}>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-500" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search cars, bookings, users..."
                className="w-full h-10 pl-10 pr-4 rounded-lg border-0 focus:outline-none focus:ring-2 focus:ring-white text-gray-900 bg-white/90 placeholder-gray-500"
              />
            </div>
          </form>
        </div>

        {/* Right section */}
        <div className="flex items-center space-x-4 ml-6">
          {/* Notifications */}
          <div className="relative">
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative p-2 text-white hover:bg-black/10 rounded-lg transition-colors"
            >
              <Bell className="h-5 w-5" />
              {unreadCount > 0 && (
                <span
                  className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 rounded-full flex items-center justify-center text-xs font-bold ring-2"
                  style={{ ringColor: primaryColor }}
                >
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            {/* Notifications Dropdown */}
            {showNotifications && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowNotifications(false)}
                />
                <div className="fixed md:absolute right-2 md:right-0 left-2 md:left-auto mt-2 md:w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-96 overflow-hidden">
                  {/* Header */}
                  <div className="p-3 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="font-semibold text-gray-900">Notifications</h3>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={toggleSound}
                        title={soundEnabled ? 'Mute notification sound' : 'Unmute notification sound'}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        {soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                      </button>
                      {unreadCount > 0 && (
                        <button
                          onClick={markAllAsRead}
                          className="text-xs text-blue-600 hover:text-blue-700"
                        >
                          Mark all as read
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Notification List */}
                  <div className="overflow-y-auto max-h-72">
                    {notifications.length > 0 ? (
                      notifications.slice(0, 10).map((notification) => (
                        <div
                          key={notification._id}
                          className={cn(
                            "p-3 border-b border-gray-100 hover:bg-gray-50 cursor-pointer",
                            !notification.isRead && "bg-blue-50"
                          )}
                          onClick={() => {
                            markAsRead(notification._id);
                            if (notification.actionUrl) {
                              navigate(notification.actionUrl);
                              setShowNotifications(false);
                            }
                          }}
                        >
                          <div className="flex items-start space-x-3">
                            <div className="flex-shrink-0 mt-1">
                              {getNotificationIcon(notification.type)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900">
                                {notification.title}
                              </p>
                              <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                                {notification.message}
                              </p>
                              <p className="text-xs text-gray-400 mt-1">
                                {formatTimeAgo(notification.createdAt)}
                              </p>
                            </div>
                            {!notification.isRead && (
                              <div className="flex-shrink-0">
                                <div className="h-2 w-2 bg-blue-600 rounded-full"></div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="p-6 text-center text-gray-500">
                        <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">No notifications yet</p>
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  {notifications.length > 0 && (
                    <div className="p-2 border-t border-gray-200">
                      <button
                        onClick={() => {
                          navigate('/notifications');
                          setShowNotifications(false);
                        }}
                        className="w-full text-center text-sm text-blue-600 hover:text-blue-700 py-1"
                      >
                        View all notifications
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* User Menu */}
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center space-x-3 p-2 hover:bg-black/10 rounded-lg transition-colors"
            >
              <div className="h-8 w-8 bg-white rounded-full flex items-center justify-center overflow-hidden">
                {user?.photo?.url ? (
                  <img
                    src={user.photo.url}
                    alt={user.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-sm font-semibold" style={{ color: primaryColor }}>{initials}</span>
                )}
              </div>
              <div className="text-left hidden md:block">
                <p className="text-sm font-medium text-white">
                  {formatName(user?.name) || 'User'}
                </p>
                <p className="text-xs text-blue-200">
                  {formatRole(user?.role) || 'User'}
                </p>
              </div>
              <ChevronDown className="h-4 w-4 text-white" />
            </button>

            {/* Dropdown Menu */}
            {showUserMenu && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowUserMenu(false)}
                />
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                  <a
                    href="/profile"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Profile
                  </a>
                  <a
                    href="/settings"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Settings
                  </a>
                  <hr className="my-1 border-gray-200" />
                  <button
                    onClick={() => {
                      localStorage.removeItem('token');
                      localStorage.removeItem('user');
                      window.location.href = '/login';
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    Logout
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
    </>
  );
};

export default TopBar;
